import { createContext, createElement, useContext, type ReactNode } from 'react'

export interface ApiSession {
  expectedToken: string
  generation: number
  userId: string
  mode: 'multi'
  isCurrent: (session: Pick<ApiSession, 'expectedToken' | 'generation' | 'userId'>) => boolean
}

type GlobalSessionState =
  | { kind: 'disabled' }
  | { kind: 'pending' }
  | { kind: 'single' }
  | { kind: 'multi'; session: ApiSession }

let globalSessionState: GlobalSessionState = { kind: 'disabled' }
const accountRequestControllers = new Set<AbortController>()

export class StaleSessionError extends Error {
  constructor() {
    super('Your sign-in changed. Please retry after the app refreshes your session.')
    this.name = 'StaleSessionError'
  }
}

export function isApiSessionCurrent(session: ApiSession): boolean {
  return session.isCurrent(session)
}

export function requireApiSession(session?: ApiSession): ApiSession | undefined {
  if (session && !isApiSessionCurrent(session)) throw new StaleSessionError()
  return session
}

/**
 * App-level dispatch fence for account-bound wrappers that have not yet grown
 * an explicit session parameter. `undefined` means multi-user identity is
 * being (re)validated; `null` is the intentional single-user exemption.
 */
export function setActiveApiSession(session: ApiSession | null | undefined): void {
  for (const controller of accountRequestControllers) controller.abort()
  accountRequestControllers.clear()
  globalSessionState = session === undefined
    ? { kind: 'pending' }
    : session === null
      ? { kind: 'single' }
      : { kind: 'multi', session }
}

export function resolveApiSession(captured?: ApiSession | null): ApiSession | undefined {
  if (captured === null) return undefined
  if (captured) return requireApiSession(captured)
  if (globalSessionState.kind === 'pending') throw new StaleSessionError()
  if (globalSessionState.kind === 'multi') return requireApiSession(globalSessionState.session)
  return undefined
}

export function registerAccountRequest(controller: AbortController): () => void {
  accountRequestControllers.add(controller)
  return () => accountRequestControllers.delete(controller)
}

const ApiSessionContext = createContext<ApiSession | undefined>(undefined)

export function ApiSessionProvider({ session, children }: { session?: ApiSession; children: ReactNode }) {
  return createElement(ApiSessionContext.Provider, { value: session }, children)
}

/** Capture this value during render and pass it to account-bound API calls. */
export function useApiSession(): ApiSession | undefined {
  return useContext(ApiSessionContext)
}

const PRIVATE_SESSION_STORAGE_PREFIXES = ['constituent.questionnaire.'] as const

export function clearPrivateSessionStorage(storage?: Storage): void {
  try {
    const target = storage ?? window.sessionStorage
    const keys: string[] = []
    for (let index = 0; index < target.length; index += 1) {
      const key = target.key(index)
      if (key && PRIVATE_SESSION_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) keys.push(key)
    }
    keys.forEach((key) => target.removeItem(key))
  } catch {
    // Storage can be denied by browser privacy policy. Auth invalidation must
    // still complete; future draft code treats recovery as unavailable.
  }
}
